import {
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdNotificationsNone } from 'react-icons/md';
import { typography } from 'theme/tokens';
import { useIsOrgManager } from '../../auth/roles';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetUnreadCount } from '../hooks/useGetUnreadCount';
import NotificationDropdown from './NotificationDropdown';

/**
 * Admin-only. The navbar renders on EVERY `/admin` page, including the three a
 * coach can reach, so without this guard a coach saw the org's admin alert feed
 * and every interaction with it (opening the dropdown marks items read, as does
 * "mark all as read") now 403s — `PATCH notifications/admin/read-all` and
 * `PATCH notifications/admin/:id/read` are `hasOrganizationAdminAccess`.
 *
 * Hiding rather than disabling is deliberate: `AdminNotification.isRead` is a
 * single org-wide flag with no recipient column, so there is no coach-scoped
 * feed to show — the bell is an admin surface end to end, and the admin
 * notification list page is already outside the coach navigation allowlist.
 */
export default function NotificationBell() {
  const isOrgManager = useIsOrgManager();
  const { selectedOrganization } = useSelectedOrganization();
  const { data } = useGetUnreadCount(
    isOrgManager ? selectedOrganization?.id : undefined,
  );
  const navbarIcon = useColorModeValue('gray.400', 'white');
  const menuBg = useColorModeValue('white', 'navy.800');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );

  const unreadCount = data?.count ?? 0;

  if (!isOrgManager) return null;

  return (
    <Popover placement="bottom-end" isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Button
              data-testid="notifications-bell"
              variant="no-hover"
              bg="transparent"
              p="0px"
              minW="unset"
              minH="unset"
              h="18px"
              w="max-content"
              me="10px"
              pe="10px"
              position="relative"
            >
              <Icon h="18px" w="18px" color={navbarIcon} as={MdNotificationsNone} />
              {unreadCount > 0 && (
                <Flex
                  position="absolute"
                  top="-6px"
                  right="-8px"
                  align="center"
                  justify="center"
                  bg="brand.500"
                  color="white"
                  borderRadius="full"
                  minW="16px"
                  h="16px"
                  px="3px"
                >
                  <Text {...typography.caption} fontSize="9px" fontWeight="700" lineHeight="1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Text>
                </Flex>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            bg={menuBg}
            borderRadius="14px"
            boxShadow={shadow}
            border="none"
            w="auto"
          >
            <PopoverBody p="0">
              <NotificationDropdown onClose={onClose} />
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
